/* eslint-disable no-console */
/* eslint-disable no-use-before-define */
const isJquery = (obj) => obj && obj.jquery;
/**
 * @typedef {Object} wdSlideout
 * @property {Function} toggleSlideout
 * @property {Function} closeSlideout
 * @property {Function} openSlideout
 * @property {Function} setSlideoutContent
 * @property {Function} showLoadingOverlay
 * @property {Function} hideLoadingOverlay
 * @property {Function} clearSlideoutContent
 * @property {jQuery} $slideout
 * @property {jQuery} $slideoutToggle
 * @property {jQuery} $slideoutClose
 * @property {jQuery} $overlay
 * @property {jQuery} $loadingOverlay
 */

/**
 * Creates a slideout instance
 * @param {Object} options
 * @param {String | jQuery} options.slideoutSelector
 * @param {String} options.slideoutOpenClass
 * @param {String} options.slideoutCloseSelector
 * @param {String | jQuery} options.slideoutToggleSelector
 * @param {String} options.pageOverlaySelector
 * @param {String} options.pageOverlayOpenClass
 * @param {String} options.loadingOverlaySelector
 * @param {String} options.loadingOverlayOpenClass
 * @param {String} options.eventNamespace
 * @param {Boolean} options.closeAllOnOpen
 * @param {Boolean} options.closeOnClickAway
 * @param {Array} options.clickAwayExceptions
 * @param {Function} options.onSlideoutOpen
 * @param {Function} options.onSlideoutClose
 * @param {Function} options.onSlideoutClickAway
 * @returns {wdSlideout}
 */
const wdSlideout = ({
    slideoutSelector = '#ob-slideout',
    slideoutOpenClass = 'ob-slideout--open',
    slideoutCloseSelector = '.ob-slideout-close',
    slideoutToggleSelector = '.ob-slideout-toggle',
    slideoutContentSelector = '.ob-slideout-content',
    pageOverlaySelector = '.ob-overlay',
    pageOverlayOpenClass = 'ob-overlay--open',
    loadingOverlaySelector = '.ob-slideout-loader',
    loadingOverlayOpenClass = 'ob-slideout-loader--loading',
    bodyNoScrollClass = 'ob-noScroll',
    scrollLockBody = true,
    showPageOverlay = true,
    eventNamespace = 'wdSlideout',
    closeAllOnOpen = true,
    closeOnClickAway = false,
    clickAwayExceptions = [],
    onSlideoutClickAway = null,
    onSlideoutOpen = null,
    onSlideoutClose = null,
    debug = false,
}) => {
    const $slideout = isJquery(slideoutSelector) ? slideoutSelector : $(slideoutSelector);
    const $slideoutToggle = isJquery(slideoutToggleSelector) ? slideoutToggleSelector : $(slideoutToggleSelector);
    const $loadingOverlay = $(loadingOverlaySelector, $slideout);
    const $slideoutClose = $(slideoutCloseSelector, $slideout);
    const $overlay = $(pageOverlaySelector);
    const $slideoutContent = $(slideoutContentSelector, $slideout);

    const slideout = {
        toggleSlideout: () => $(document).trigger(`${eventNamespace}.toggle`),
        closeSlideout: () => $(document).trigger(`${eventNamespace}.close`),
        openSlideout: () => $(document).trigger(`${eventNamespace}.open`),
        setSlideoutContent,
        showLoadingOverlay,
        hideLoadingOverlay,
        clearSlideoutContent,
        $slideout,
        $slideoutToggle,
        $loadingOverlay,
        $overlay,
    };

    $slideoutToggle.on('click', (e) => {
        e.preventDefault();
        toggleSlideout();
    });

    $slideoutClose.on('click', (e) => {
        e.preventDefault();
        closeSlideout();
    });

    $(document).on('wdSlideoutGlobal.close', () => {
        if (debug) {
            console.info('wdSlideoutGlobal.close');
        }
        closeSlideout();
    });

    $(document).on(`${eventNamespace}.toggle`, () => {
        if (debug) {
            console.info('wdSlideout.toggle', slideout);
        }

        toggleSlideout();
    });

    $(document).on(`${eventNamespace}.close`, () => {
        if (debug) {
            console.info('wdSlideout.close', slideout);
        }
        closeSlideout();
    });
    $(document).on(`${eventNamespace}.open`, () => {
        if (debug) {
            console.info('wdSlideout.open', slideout);
        }
        openSlideout();
    });

    $(document).on(`${eventNamespace}.closed`, () => {
        if (debug) {
            console.info('wdSlideout.closed', slideout);
        }
    });

    $(document).on(`${eventNamespace}.opened`, () => {
        if (debug) {
            console.info('wdSlideout.opened', slideout);
        }
    });

    function onClickAway(e) {
        const slideOutOpen = $slideout.hasClass(slideoutOpenClass);

        if (!slideOutOpen) {
            return;
        }

        if (onSlideoutClickAway) {
            return onSlideoutClickAway(e, slideout);
        }

        if (!closeOnClickAway) {
            return;
        }

        const $clickTarget = $(e.target);

        // If click target is within the slideout, do nothing
        if ($clickTarget.closest($slideout).length > 0) {
            return;
        }

        // If click target is within the slideout toggle, do nothing
        if ($clickTarget.closest($slideoutToggle).length > 0) {
            return;
        }

        // if the click target is within the clickAwayExceptions, do nothing
        if (clickAwayExceptions.some((selector) => $clickTarget.closest(selector).length > 0)) {
            return;
        }

        closeSlideout();
    }

    function clearSlideoutContent() {
        $slideoutContent.html('');
    }

    function closeSlideout() {
        if (!$slideout.hasClass(slideoutOpenClass)) {
            return;
        }

        if (showPageOverlay) {
            $overlay.removeClass(pageOverlayOpenClass);
        }

        if (scrollLockBody) {
            $('body').removeClass(bodyNoScrollClass);
        }

        $slideout.removeClass(slideoutOpenClass);
        $slideout.attr('aria-hidden', true);
        $(document).off('click', onClickAway);
        $(document).trigger(`${eventNamespace}.closed`);

        if (onSlideoutClose) {
            onSlideoutClose(slideout);
        }
    }

    function openSlideout() {
        if (closeAllOnOpen) {
            $(document).trigger('wdSlideoutGlobal.close');
        }
        
        if (scrollLockBody) {
            $('body').addClass(bodyNoScrollClass);
        }

        $slideout.addClass(slideoutOpenClass);
        $slideout.attr('aria-hidden', false);

        if (showPageOverlay) {
            $overlay.addClass(pageOverlayOpenClass);
        }

        $(document).on('click', onClickAway);
        $(document).trigger(`${eventNamespace}.opened`);

        if (onSlideoutOpen) {
            onSlideoutOpen(slideout);
        }
    }

    function setSlideoutContent(content) {
        if (content) {
            $slideout.find(slideoutContentSelector).html(content);
        }
    }

    function toggleSlideout() {
        if ($slideout.hasClass(slideoutOpenClass)) {
            return closeSlideout();
        }

        return openSlideout();
    }

    function showLoadingOverlay() {
        $loadingOverlay.addClass(loadingOverlayOpenClass);
    }

    function hideLoadingOverlay() {
        $loadingOverlay.removeClass(loadingOverlayOpenClass);
    }

    return slideout;
};

export default wdSlideout;
